import { backendApis } from '@/backendApis';
import { ModalsManager } from '@/components/modals/modalsManager';
import { IntegrationConnectionModal } from '@/integrations/integrations.modals';
import { currentPageReturnTo } from '@/integrations/oauth-return';

import type { OAuthReturnTo } from '@littlebird/mcp';

export {
  handleEventKitConnect,
  handleOpenEventKitSettings,
} from './eventkit-handler';
export {
  handleContactsConnect,
  handleOpenContactsSettings,
} from './contacts-handler';

export interface OAuthConnectResult {
  success: boolean;
  needsOAuth?: boolean;
  error?: string;
}

// Only one connection flow at a time. The isOpen check alone is not enough:
// between `await connectIntegration` and the modal being shown there is a window
// where a second concurrent caller passes the check and fires another backend
// connect, producing duplicate pending accounts / orphaned OAuth flows (the exact
// burst-reconnect failure this guards against). A synchronous in-flight flag, set
// before the await and cleared once the modal is up, closes that window; `isOpen`
// then covers the period the modal stays open.
let oauthConnectInFlight = false;

/**
 * Initiates an OAuth connection for an integration.
 * Shows the connection modal and returns while OAuth completes asynchronously.
 */
export async function connectOAuthIntegration(
  toolkit_name: string,
  display_name: string,
  account_label?: string | null,
  onOAuthComplete?: (success: boolean) => void,
  returnTo?: OAuthReturnTo,
  // Exact account to reconnect. Labels are user-editable, so selecting by
  // label alone breaks after a rename; the backend prefers this when present.
  account_id?: string | null
): Promise<OAuthConnectResult> {
  if (
    oauthConnectInFlight ||
    ModalsManager.isOpen(IntegrationConnectionModal)
  ) {
    return { success: false, error: 'A connection is already in progress' };
  }
  oauthConnectInFlight = true;
  try {
    const connectResponse = await backendApis.integrations.connectIntegration(
      toolkit_name,
      account_label,
      returnTo ?? currentPageReturnTo(),
      account_id
    );

    if (connectResponse.success && connectResponse.redirect_url) {
      ModalsManager.show(
        IntegrationConnectionModal,
        {
          redirectUrl: connectResponse.redirect_url,
          toolkitName: toolkit_name,
          displayName: display_name,
          onComplete: onOAuthComplete,
          accountId: connectResponse.account_id,
          flowId: connectResponse.flow_id,
          accountLabel: account_label,
        },
        { unique: true }
      );
      return { success: true, needsOAuth: true };
    } else {
      onOAuthComplete?.(false);
      return {
        success: false,
        error: `Failed to connect ${display_name}`,
      };
    }
  } catch (error) {
    console.error('OAuth connect error:', error);
    onOAuthComplete?.(false);
    return {
      success: false,
      error: `Failed to connect ${display_name}`,
    };
  } finally {
    oauthConnectInFlight = false;
  }
}
